import {
    Action,
    DEFAULT_OPEN_CHARACTER_CREATOR_ACTION,
    DEFAULT_OPEN_CHARACTER_EXPRESSION_ACTION,
    DEFAULT_OPEN_DISTRICT_ACTION,
    DEFAULT_OPEN_IN_STORE_ACTION,
    DEFAULT_OPEN_LOCATION_ACTION,
    DEFAULT_OPEN_OUTFIT_DESIGNER_ACTION,
    DEFAULT_OPEN_PORTAL_ACTION,
    DEFAULT_OPEN_SHOP_ACTION,
    getDefaultButton,
} from '../../model/components/ActionButton';
import { ArticlePage } from '../../model/components/ArticlePage';
import { isArticlePost } from '../../model/PostTypes';
import { PathContextProvider } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { ActionButtonInput } from './ActionButtonInput';
import { MediaInput } from './MediaInput';
import { TextTypeInput } from './TextTypeInput';

import { assert } from '../../../utils/assert';

export const BUTTON_ACTION_OPTIONS: Array<{
    value: Action['$type'] | 'none';
    label: string;
}> = [
    {
        value: 'none',
        label: 'No Button',
    },
    {
        value: 'OpenShop',
        label: 'Open Shop Button',
    },
    {
        value: 'OpenLocation',
        label: 'Open Location Button',
    },
    {
        value: 'OpenDistrict',
        label: 'Open District Button',
    },
    {
        value: 'OpenCharacterCreator',
        label: 'Open Character Creator',
    },
    {
        value: 'OpenOutfitDesigner',
        label: 'Open Outfit Designer',
    },
    {
        value: 'OpenCEX',
        label: 'Open Character Expression',
    },
    {
        value: 'OpenPortal',
        label: 'Open Portal (N.B. Only for featured posts)',
    },
    {
        value: 'OpenInStore',
        label: 'Open In Store',
    },
] as const;

export interface ArticlePageInputProps {
    value: ArticlePage;
    onChange?: (value: ArticlePage) => void;
    button?: {
        enabled?: boolean;
        allowedActions?: Array<Action['$type'] | 'none'>;
    };
    pageIndex: number;
}
export const ArticlePageInput = ({
    value,
    onChange,
    button,
    pageIndex,
}: ArticlePageInputProps) => {
    const onFieldChange = <Field extends keyof ArticlePage>(
        field: Field,
        fieldValue: ArticlePage[Field],
    ) => {
        onChange?.({
            ...value,
            [field]: fieldValue,
        });
    };

    const onButtonOptionChange = (option: string) => {
        switch (option as (typeof BUTTON_ACTION_OPTIONS)[number]['value']) {
            case 'OpenShop':
                onChange?.({
                    ...value,
                    button: getDefaultButton(DEFAULT_OPEN_SHOP_ACTION),
                });
                break;
            case 'OpenDistrict':
                onChange?.({
                    ...value,
                    button: getDefaultButton(DEFAULT_OPEN_DISTRICT_ACTION),
                });
                break;
            case 'OpenLocation':
                onChange?.({
                    ...value,
                    button: getDefaultButton(DEFAULT_OPEN_LOCATION_ACTION),
                });
                break;
            case 'OpenCharacterCreator':
                onChange?.({
                    ...value,
                    button: getDefaultButton(
                        DEFAULT_OPEN_CHARACTER_CREATOR_ACTION,
                    ),
                });
                break;
            case 'OpenOutfitDesigner':
                onChange?.({
                    ...value,
                    button: getDefaultButton(
                        DEFAULT_OPEN_OUTFIT_DESIGNER_ACTION,
                    ),
                });
                break;
            case 'OpenCEX':
                onChange?.({
                    ...value,
                    button: getDefaultButton(
                        DEFAULT_OPEN_CHARACTER_EXPRESSION_ACTION,
                    ),
                });
                break;
            case 'OpenPortal':
                onChange?.({
                    ...value,
                    button: getDefaultButton(DEFAULT_OPEN_PORTAL_ACTION),
                });
                break;
            case 'OpenInStore':
                onChange?.({
                    ...value,
                    button: getDefaultButton(DEFAULT_OPEN_IN_STORE_ACTION),
                });
                break;
            case 'none':
                onChange?.({
                    ...value,
                    button: undefined,
                });
                break;
        }
    };

    const { post, validations } = usePortalPostEditor();
    assert(
        isArticlePost(post),
        'ArticlePageInput must be rendered with an Article post',
    );

    return (
        <>
            <fieldset>
                <legend>Background</legend>

                <MediaInput
                    value={value.background}
                    onChange={(background) =>
                        onFieldChange('background', background)
                    }
                />
            </fieldset>
            <TextTypeInput
                value={value}
                onChange={onChange}
                pageIndex={pageIndex}
                validations={validations}
            />
            {button?.enabled && (
                <fieldset>
                    <legend>Button</legend>
                    <select
                        value={
                            value.button ? value.button.action.$type : 'none'
                        }
                        onChange={(e) => onButtonOptionChange(e.target.value)}
                    >
                        {BUTTON_ACTION_OPTIONS.filter((option) => {
                            return button?.allowedActions?.includes(
                                option.value,
                            );
                        }).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {value.button && (
                        <PathContextProvider segment="button">
                            <ActionButtonInput
                                value={value.button}
                                onChange={(button) =>
                                    onFieldChange('button', button)
                                }
                            />
                        </PathContextProvider>
                    )}
                </fieldset>
            )}
        </>
    );
};
